/**
 * useRemoveUser Hook
 *
 * React Query mutation hook for removing a user from a group.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { groupService } from '../../../api/group.service';
import { getErrorMessage } from '../../../lib/utils/errors';

interface RemoveUserVariables {
  groupId: string;
  userId: string;
}

interface RemoveUserResponse {
  message: string;
  groupId: string;
  userId: string;
  updatedAt: string;
}

/**
 * Hook for removing a user from a group
 */
export const useRemoveUser = (
  tenantId: string
): UseMutationResult<RemoveUserResponse, Error, RemoveUserVariables> => {
  const queryClient = useQueryClient();

  return useMutation<RemoveUserResponse, Error, RemoveUserVariables>({
    mutationFn: ({ groupId, userId }) => {
      console.log('[useRemoveUser] Removing user from group:', groupId, userId);
      return groupService.removeUser(tenantId, groupId, userId);
    },
    onSuccess: (_, variables) => {
      console.log('[useRemoveUser] User removed successfully');
      // Invalidate group to refresh user list
      queryClient.invalidateQueries({ queryKey: ['group', tenantId, variables.groupId] });
      // Invalidate groups list
      queryClient.invalidateQueries({ queryKey: ['groups', tenantId] });
      // Invalidate user to refresh their groups array
      queryClient.invalidateQueries({ queryKey: ['user', tenantId, variables.userId] });
      // Invalidate users list
      queryClient.invalidateQueries({ queryKey: ['users', tenantId] });
    },
    onError: (error) => {
      console.error('[useRemoveUser] Failed to remove user:', getErrorMessage(error));
    },
  });
};
